import { IkeToken, type TagJSONObject } from '@ikegps/core-api';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { buildMixedFormsBreakdown } from './verify.mixed.forms';
import { VerifyMixedFormsModal } from './verify.mixed.forms.modal';

interface VerifyContext {
	origin: string;
	orgId: string;
	departmentId: string;
}

/** Verify Workbench can open at most this many collections at once, so larger jobs are excluded (UFO-794). */
const MAX_VERIFY_POLES = 1000;

export class VerifyWorkbenchCtrl {
	/**
	 * True when the feature is enabled, the origin + org/department context needed to build a link
	 * are loaded, and the job is within the pole limit. Buttons gate their rendering on this, so the
	 * link can never be incomplete and oversized jobs never offer the button.
	 */
	static canOpenInVerify(job: TagJSONObject): boolean {
		return (
			Services.Token.has(IkeToken.FEATURE_DISPLAY_VERIFY_LINK) &&
			VerifyWorkbenchCtrl.resolveContext() != null &&
			VerifyWorkbenchCtrl.withinPoleLimit(job)
		);
	}

	/**
	 * Whether the job has at most {@link MAX_VERIFY_POLES} collections. Hidden too while the job's
	 * summary is still loading: until the count is known we can't confirm it is within the limit, so
	 * we don't offer an action Verify Workbench may reject (UFO-794).
	 */
	private static withinPoleLimit(job: TagJSONObject): boolean {
		const poleCount = AppStore.TagSummary.get(job.objectId)?.revisions.length;
		return poleCount != null && poleCount <= MAX_VERIFY_POLES;
	}

	/**
	 * Opens an entire job in Verify Workbench, unless its collections span more than one form or
	 * form version, in which case a blocking popup is shown instead (UFO-1732).
	 */
	static async openJobInVerify(job: TagJSONObject): Promise<void> {
		const context = VerifyWorkbenchCtrl.resolveContext();
		if (context == null) {
			return;
		}

		const breakdown = await VerifyWorkbenchCtrl.getMixedFormsBreakdown(job);
		if (breakdown != null) {
			VerifyMixedFormsModal.open(job.name, breakdown);
			return;
		}

		const url = new URL('/verify/map', context.origin);
		url.searchParams.set('orgId', context.orgId);
		url.searchParams.set('departmentId', context.departmentId);
		url.searchParams.set('jobId', job.objectId);
		Services.Window.open(url.toString(), '_blank');
	}

	/** The job's mixed-form breakdown, or null when all of its collections share the same form revision. */
	private static async getMixedFormsBreakdown(job: TagJSONObject) {
		const revisions = AppStore.TagSummary.get(job.objectId)?.revisions ?? [];
		const collections = await Promise.all(revisions.map((revision) => AppStore.CollectionSummary.fetch(revision)));
		return buildMixedFormsBreakdown(collections);
	}

	/**
	 * The committed `verify.workbench.url` origin plus the current org/department objectIds
	 * (Parse objectIds), or null while any of them is still unloaded.
	 */
	private static resolveContext(): VerifyContext | null {
		const origin = Services.Config.config.getString('verify.workbench.url');
		const orgId = Services.Department.currentOrg?.objectId;
		const departmentId = Services.Department.current?.objectId;

		if (!origin || orgId == null || departmentId == null) {
			return null;
		}

		return { origin, orgId, departmentId };
	}
}
